import type { RiskFlowAlert } from "./riskflow-feed";
import {
  parseRiskFlowEconPrint,
  type RiskFlowEconPrintAnalysis,
} from "./riskflow-econ-print-analysis";
import { extractYouTubeVideoId } from "./youtube";

export type RiskFlowVisualSource =
  | "econ-history"
  | "source-media"
  | "sentiment-index"
  | "ticker-lightweight-chart"
  | "youtube-live";

export interface RiskFlowVisualCandidate {
  kind: "econ-history" | "image" | "stock-chart";
  imageUrl?: string;
  href: string;
  label: string;
  source: RiskFlowVisualSource;
  fit: "cover" | "contain";
  ticker?: string;
  econPrint?: RiskFlowEconPrintAnalysis;
}

const NON_EQUITY_SYMBOLS = new Set([
  "6E",
  "BTC",
  "CL",
  "ES",
  "GC",
  "NG",
  "NQ",
  "RTY",
  "SI",
  "YM",
]);

function isHttpsUrl(value: string | null | undefined): value is string {
  return Boolean(value && /^https:\/\//i.test(value));
}

function isGeneratedImageUrl(
  value: string | null | undefined,
): value is string {
  return Boolean(value && /^data:image\/svg\+xml,/i.test(value));
}

function isRenderableImageUrl(
  value: string | null | undefined,
): value is string {
  return isHttpsUrl(value) || isGeneratedImageUrl(value);
}

function isFinancialJuiceUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    return new URL(value).hostname.toLowerCase().includes("financialjuice");
  } catch {
    return false;
  }
}

function isLikelyLogoOrPlaceholderMedia(
  value: string | null | undefined,
): boolean {
  if (!value) return false;
  try {
    const url = new URL(value);
    const haystack = `${url.hostname} ${url.pathname}`.toLowerCase();
    return /\b(?:avatar|brand|default|favicon|icon|logo|placeholder|profile|sprite|wordmark)\b/.test(
      haystack.replace(/[^a-z0-9]+/g, " "),
    );
  } catch {
    return /\b(?:avatar|brand|default|favicon|icon|logo|placeholder|profile|sprite|wordmark)\b/i.test(
      value,
    );
  }
}

function sourceHref(alert: RiskFlowAlert, fallback: string): string {
  if (isHttpsUrl(alert.url) && !isFinancialJuiceUrl(alert.url)) {
    return alert.url;
  }
  return fallback;
}

function normalizeTicker(raw: string): string | null {
  const ticker = raw.replace(/^\$/, "").trim().toUpperCase();
  if (!/^[A-Z][A-Z0-9.]{0,5}$/.test(ticker)) return null;
  if (NON_EQUITY_SYMBOLS.has(ticker)) return null;
  return ticker;
}

function hasPredictionMarketSource(alert: RiskFlowAlert): boolean {
  if (alert.source === "kalshi-whale" || alert.source === "polymarket") {
    return true;
  }
  if (
    alert.tags.some((tag) => /prediction.market|kalshi|polymarket/i.test(tag))
  ) {
    return true;
  }
  if (!alert.url) return false;
  return /kalshi|polymarket/i.test(alert.url);
}

function isCryptoFearGreedAlert(alert: RiskFlowAlert): boolean {
  const text = `${alert.headline} ${alert.summary} ${alert.tags.join(" ")}`;
  return /crypto\s+fear\s*(?:&|and)?\s*greed\s+index/i.test(text);
}

function youtubeLiveCandidate(
  alert: RiskFlowAlert,
): RiskFlowVisualCandidate | null {
  const text = `${alert.headline} ${alert.summary} ${alert.tags.join(" ")}`;
  if (!/\bWATCH\s+LIVE\b/i.test(text)) return null;
  const videoId =
    extractYouTubeVideoId(alert.videoUrl) || extractYouTubeVideoId(alert.url);
  if (!videoId) return null;
  return {
    kind: "image",
    imageUrl: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
    href: `https://www.youtube.com/watch?v=${videoId}`,
    label: "Watch live on YouTube",
    source: "youtube-live",
    fit: "cover",
  };
}

function cryptoFearGreedScore(alert: RiskFlowAlert): number | null {
  const text = `${alert.headline} ${alert.summary}`;
  const match = text.match(/\b(\d{1,3})\s*\/\s*100\b/);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, value));
}

function cryptoFearGreedLabel(score: number | null, alert: RiskFlowAlert) {
  const text = `${alert.headline} ${alert.summary}`;
  if (/extreme\s+fear/i.test(text)) return "Extreme Fear";
  if (/extreme\s+greed/i.test(text)) return "Extreme Greed";
  if (/\bfear\b/i.test(text)) return "Fear";
  if (/\bgreed\b/i.test(text)) return "Greed";
  if (score == null) return "Fear & Greed";
  if (score <= 24) return "Extreme Fear";
  if (score <= 49) return "Fear";
  if (score <= 74) return "Greed";
  return "Extreme Greed";
}

export function riskFlowMentionedTickers(alert: RiskFlowAlert): string[] {
  const seen = new Set<string>();
  const tickers: string[] = [];
  const addTicker = (raw: string) => {
    const ticker = normalizeTicker(raw);
    if (!ticker || seen.has(ticker)) return;
    seen.add(ticker);
    tickers.push(ticker);
  };

  for (const symbol of alert.symbols ?? []) {
    addTicker(symbol);
  }
  const headlineTickers =
    alert.headline.match(/\$[A-Z][A-Z0-9.]{0,5}\b/g) ?? [];
  for (const symbol of headlineTickers) {
    addTicker(symbol);
  }
  return tickers;
}

function tickerCandidates(alert: RiskFlowAlert): RiskFlowVisualCandidate[] {
  return riskFlowMentionedTickers(alert).map((ticker) => {
    const encoded = encodeURIComponent(ticker);
    return {
      kind: "stock-chart" as const,
      href: `https://finance.yahoo.com/quote/${encoded}`,
      label: `${ticker} embedded stock chart`,
      source: "ticker-lightweight-chart" as const,
      fit: "cover" as const,
      ticker,
    };
  });
}

function econHistoryCandidates(
  alert: RiskFlowAlert,
): RiskFlowVisualCandidate[] {
  const econPrint = parseRiskFlowEconPrint(alert);
  if (!econPrint) return [];
  return [
    {
      kind: "econ-history",
      href: `#riskflow-econ-history-${econPrint.key.toLowerCase()}`,
      label: `${econPrint.title} econ history`,
      source: "econ-history",
      fit: "cover",
      econPrint,
    },
  ];
}

function cryptoFearGreedSnapshot(alert: RiskFlowAlert): string {
  const score = cryptoFearGreedScore(alert);
  const label = cryptoFearGreedLabel(score, alert);
  const marker = score == null ? 50 : score;
  const markerX = 54 + marker * 5.56;
  const scoreLabel = score == null ? "--" : String(score);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 680 360">
<rect width="680" height="360" rx="18" fill="#050402"/>
<rect x="1" y="1" width="678" height="358" rx="17" fill="none" stroke="#c79f4a" stroke-opacity=".22"/>
<text x="34" y="48" fill="#f0ead6" fill-opacity=".78" font-family="monospace" font-size="17" font-weight="700" letter-spacing="3">CRYPTO FEAR &amp; GREED</text>
<defs>
<linearGradient id="rainbowFuse" x1="0" x2="1">
<stop offset="0%" stop-color="#ff3b3b"/>
<stop offset="32%" stop-color="#ff9f1c"/>
<stop offset="58%" stop-color="#f4d35e"/>
<stop offset="78%" stop-color="#45d483"/>
<stop offset="100%" stop-color="#12d6a4"/>
</linearGradient>
</defs>
<rect x="54" y="82" width="556" height="24" rx="12" fill="#15120a"/>
<rect x="54" y="82" width="556" height="24" rx="12" fill="url(#rainbowFuse)"/>
<g opacity=".55" stroke="#050402" stroke-width="1">
<line x1="193" x2="193" y1="79" y2="110"/>
<line x1="332" x2="332" y1="79" y2="110"/>
<line x1="471" x2="471" y1="79" y2="110"/>
</g>
<circle cx="${markerX.toFixed(1)}" cy="94" r="15" fill="#f0ead6"/>
<circle cx="${markerX.toFixed(1)}" cy="94" r="9" fill="#050402"/>
<text x="340" y="193" text-anchor="middle" fill="#f0ead6" font-family="'Doto','Readable Digits',monospace" font-size="92" font-weight="800">${scoreLabel}</text>
<text x="412" y="186" fill="#f0ead6" fill-opacity=".45" font-family="'Doto','Readable Digits',monospace" font-size="24">/100</text>
<text x="340" y="226" text-anchor="middle" fill="#c79f4a" font-family="monospace" font-size="16" font-weight="700" letter-spacing="2">${label.toUpperCase()}</text>
<text x="54" y="281" fill="#ff6b6b" font-family="monospace" font-size="13" font-weight="700">EXTREME FEAR</text>
<text x="238" y="281" fill="#ffb84d" font-family="monospace" font-size="13" font-weight="700">FEAR</text>
<text x="380" y="281" fill="#b8d957" font-family="monospace" font-size="13" font-weight="700">GREED</text>
<text x="494" y="281" fill="#22d6a0" font-family="monospace" font-size="13" font-weight="700">EXTREME GREED</text>
<text x="34" y="326" fill="#f0ead6" fill-opacity=".36" font-family="monospace" font-size="12">Sentiment fuse keyed from the reported index print</text>
</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function cryptoFearGreedCandidates(
  alert: RiskFlowAlert,
): RiskFlowVisualCandidate[] {
  return [
    {
      kind: "image",
      imageUrl: cryptoFearGreedSnapshot(alert),
      href: "https://alternative.me/crypto/fear-and-greed-index/",
      label: "Crypto Fear & Greed Index fuse",
      source: "sentiment-index",
      fit: "cover",
    },
  ];
}

function dedupeCandidates(
  candidates: Array<RiskFlowVisualCandidate | null>,
): RiskFlowVisualCandidate[] {
  const seen = new Set<string>();
  const deduped: RiskFlowVisualCandidate[] = [];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const key =
      candidate.kind === "econ-history"
        ? `${candidate.source}:${candidate.econPrint?.key ?? candidate.href}`
        : candidate.kind === "stock-chart"
          ? `${candidate.source}:${candidate.ticker ?? candidate.href}`
          : isRenderableImageUrl(candidate.imageUrl)
            ? candidate.imageUrl
            : null;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    deduped.push(candidate);
  }
  return deduped;
}

export function riskFlowVisualCandidates(
  alert: RiskFlowAlert,
): RiskFlowVisualCandidate[] {
  const youtubeLiveVisual = dedupeCandidates([youtubeLiveCandidate(alert)]);
  if (youtubeLiveVisual.length > 0) return youtubeLiveVisual;

  const tickerVisuals = dedupeCandidates(tickerCandidates(alert));
  if (tickerVisuals.length > 0) return tickerVisuals;

  const econVisuals = dedupeCandidates(econHistoryCandidates(alert));
  if (econVisuals.length > 0) return econVisuals;

  const candidates = dedupeCandidates([
    isHttpsUrl(alert.imageUrl) &&
    !isLikelyLogoOrPlaceholderMedia(alert.imageUrl)
      ? {
          imageUrl: alert.imageUrl,
          href: sourceHref(alert, alert.imageUrl),
          label: "source media",
          source: "source-media",
          kind: "image",
          fit: "contain",
        }
      : null,
    ...(isCryptoFearGreedAlert(alert) ? cryptoFearGreedCandidates(alert) : []),
  ]);
  if (candidates.length > 0) return candidates;
  if (hasPredictionMarketSource(alert)) return [];
  return [];
}
